/**
 * The whole assistant: one button that flies out of the corner, listens, and
 * flies back with the answer.
 *
 * The version before this was a panel with a greeting, a conversation, a text
 * box, a send button and a row of suggestions — every one of them a decision to
 * make before you could ask for help, which for the person this is built for is
 * the same problem they pressed Help about. The version after *that* was this
 * screen with a microphone waiting on it, which was better and still had one
 * step too many: nobody presses Help in order to look at a microphone.
 *
 * So now the corner button *is* the microphone, and pressing it is asking. The
 * screen dims, the button arrives in the middle already listening, rippling as it
 * hears them, and the words appear above it as they are spoken. Stop talking for
 * four seconds and the question goes on its own; press the tick and it goes at
 * once. A ring turns round the button while the answer is worked out. Then the
 * screen clears, the button flies home to its corner, and the answer sits in a
 * band across the top while it is read out — so whatever it has just done to the
 * screen underneath is visible *while* it is being explained. The ✕ in the corner
 * stops all of it, at any point.
 *
 * ## One skeleton, four stages
 *
 * Every stage renders the *same four slots* — something said, the button, a line
 * of guidance, and whatever else that stage needs — rather than each swapping in
 * a screen of its own. Keeping the button at one fixed position in the tree means
 * React keeps the same DOM node from the moment it leaves the corner to the
 * moment it returns, which is what lets it be animated as one moving object
 * rather than two that appear and disappear.
 *
 * ## The rules this screen holds to
 *
 *  - **Never more than one obvious thing to press.** Everything else is small,
 *    quiet, and at the edges.
 *  - **Nothing to read while deciding.** One line of guidance at a time.
 *  - **What is said gets a fixed box**, so a long question cannot shove the
 *    microphone off the bottom of a phone mid-sentence.
 *  - **Always a way out.** One ✕ in the corner, at every stage, always meaning
 *    "stop everything".
 *  - **A keyboard when there is no microphone.** Firefox has no speech
 *    recognition, and some people would simply rather type.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { useI18n } from '../i18n';
import { useAssistant, type Answer, type Origin } from './AssistantState';
import { answeredBy, describeSetup } from './settings';

/**
 * How long the band stays after the voice has stopped.
 *
 * Long enough to finish reading a sentence you were half-way through when the
 * speaking ended, short enough that it is gone before it becomes furniture.
 */
const LINGER_MS = 2000;
/** …and how long it stays when nothing is being read out, so there is no voice to wait for. */
const SILENT_LINGER_MS = 6000;

export function AssistantOverlay() {
  const { t, lang } = useI18n();
  const a = useAssistant();
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const typeRef = useRef<HTMLInputElement | null>(null);
  /*
   * The flight is carried by a wrapper, not by the button.
   *
   * The button's own `transform` is already spoken for: the stop face breathes
   * in and out on a keyframe animation, and a running animation beats anything a
   * class sets, so a flight applied to the button itself was simply erased the
   * moment it started recording. One extra element, and the two never argue.
   */
  const flyRef = useRef<HTMLDivElement | null>(null);

  const { stage, answer, origin } = a;

  /*
   * The answer is a band on the real screen, not a page of its own — except
   * when it is a complaint form, which has a field in it and needs the quiet.
   */
  const banded = stage === 'answer' && !answer?.offer;
  const formShown = stage === 'answer' && !!answer?.offer;

  /*
   * Make room for the form.
   *
   * Every slot above the bottom one is positioned from `--assist-extras-h`, the
   * height of that bottom slot — which is right for a cancel link and wrong for
   * a form several lines tall: the form grew upwards and the microphone and the
   * answer sat on top of it. So while the form is up, that variable is set to the
   * form's real height, and everything above it moves up by exactly as much.
   */
  const stageRef = useRef<HTMLDivElement | null>(null);
  const extrasRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const stageEl = stageRef.current;
    const extras = extrasRef.current;
    if (!stageEl || !extras || !formShown) return;
    const fit = () => stageEl.style.setProperty('--assist-extras-h', `${extras.offsetHeight}px`);
    fit();
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    watch?.observe(extras);
    return () => {
      watch?.disconnect();
      stageEl.style.removeProperty('--assist-extras-h');
    };
  }, [formShown]);

  // A browser that cannot listen starts on the keyboard rather than showing a
  // microphone that would do nothing.
  useEffect(() => {
    if (stage === 'ready' && !a.canListen) setTyping(true);
  }, [stage, a.canListen]);
  useEffect(() => {
    if (typing) typeRef.current?.focus();
  }, [typing, stage]);

  /*
   * The flight out of the corner.
   *
   * Done by hand rather than with a keyframe because the corner is not in a
   * fixed place: it is top-left in English, top-right in Hebrew, and a different
   * number of pixels down on a phone than on a desktop. So the button is put
   * *at* the corner with no transition, the browser is given one frame to paint
   * it there, and then the override is removed — and the ordinary transition on
   * the button carries it to the middle. Two nested `requestAnimationFrame`s,
   * because removing the class in the first one gets folded into the same style
   * recalculation and the browser never paints the starting position at all,
   * which shows up as the button simply appearing in the middle.
   */
  useLayoutEffect(() => {
    const el = flyRef.current;
    if (!a.open || !el || !origin) return;
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    el.style.setProperty('--assist-fly-x', `${origin.x - (r.left + r.width / 2)}px`);
    el.style.setProperty('--assist-fly-y', `${origin.y - (r.top + r.height / 2)}px`);
    el.style.setProperty('--assist-fly-s', `${Math.max(0.12, origin.size / r.width)}`);
    el.classList.add('is-at-corner');
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => el.classList.remove('is-at-corner'));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
    // Once per opening. This component is mounted for the life of the app and
    // merely renders nothing while closed, so `[]` would have run it once at
    // startup and never again — the flight would have worked exactly zero times.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.open, origin]);

  /*
   * The band goes when the voice does.
   *
   * It has to be tied to the speaking rather than to a fixed number of seconds,
   * because an answer of one sentence and an answer of three take very different
   * amounts of time to read and both have to still be on screen at the end. When
   * nothing is being read out there is nothing to wait for, so it falls back to
   * a plain count.
   *
   * `speaking` is already true on the render that first shows the band — the ask
   * loop sets the stage and starts the voice in the same tick — so this does not
   * race with the start of the reading.
   */
  const closeRef = useRef(a.close);
  closeRef.current = a.close;
  useEffect(() => {
    if (!banded || a.speaking) return;
    const id = setTimeout(() => closeRef.current(), a.speaksAloud ? LINGER_MS : SILENT_LINGER_MS);
    return () => clearTimeout(id);
    // `a` itself is deliberately not a dependency. The app state above this
    // re-renders several times a second while the tree is on screen, and a timer
    // torn down and restarted on every one of those renders is a timer that never
    // fires — the band would have sat there for ever.
  }, [banded, a.speaking, a.speaksAloud]);

  // Escape closes it, like every other overlay in the app.
  useEffect(() => {
    if (!a.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        a.close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [a]);

  /*
   * The page underneath must not scroll while the screen is dimmed — but it
   * must once the band is up, because the band is on top of a screen the person
   * is now meant to be looking at and using.
   */
  useEffect(() => {
    if (!a.open || banded) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [a.open, banded]);

  if (!a.open) return null;

  /*
   * One button, three faces. It never moves between them, because a control that
   * jumps about mid-task is a control people stop trusting.
   *
   *   mic    nothing is happening — only ever seen on a browser that cannot hear
   *   done ✓ listening, rippling as it hears them; press it and the question goes
   *   think  the question has gone: a ring turning round the edge until the answer
   *          comes. Nothing to press — the ✕ in the corner calls it back
   *
   * The listening face used to be a red square. Red and a square both say "stop"
   * and "cancel", so people pressed it expecting to throw the question away and
   * sent it instead. A tick in the app's own colour says what pressing it does.
   */
  const face: ButtonFace = stage === 'listening' ? 'done' : stage === 'thinking' ? 'think' : 'mic';

  // ---- above the button: what is being said ----
  let said: React.ReactNode = null;
  if (stage === 'listening' || stage === 'thinking') {
    said = (
      <p className={`assist-heard-live ${a.transcript ? '' : 'is-waiting'}`} aria-live="polite">
        <span>{a.transcript || t('assistant.listening')}</span>
      </p>
    );
  } else if (stage === 'answer' && answer?.text) {
    // Only ever seen behind a complaint form — every other answer is a band.
    said = (
      <p className={`assist-answer ${answer.error ? 'is-error' : ''}`} aria-live="polite">
        {answer.text}
      </p>
    );
  } else if (stage === 'ready') {
    said = <p className="assist-prompt">{t('assistant.greeting')}</p>;
  }

  /*
   * One line under it, or none.
   *
   * "Tap and ask" and "or hold it while you talk" both went when the corner
   * button started the recording: there is no tap to describe any more, and
   * holding is gone. What is left is the one instruction that genuinely has to
   * be read — a recording that does not end by itself is one somebody will sit
   * and wait through — and it is marked urgent for that reason.
   */
  let hint = '';
  let urgent = false;
  if (stage === 'listening') {
    hint = t('assistant.tapToStop');
    urgent = true;
  } else if (stage === 'thinking') hint = t('assistant.working');

  const label = face === 'think' ? t('assistant.working') : face === 'done' ? t('assistant.tapToStop') : t('assistant.tapToTalk');

  return (
    <>
      <div
        className={`assist-veil ${banded ? 'is-cleared' : ''}`}
        role={banded ? undefined : 'dialog'}
        aria-modal={banded ? undefined : true}
        aria-label={t('assistant.title')}
        aria-hidden={banded || undefined}
      >
        {/*
          * Who is about to answer, said quietly and always.
          *
          * Without it the screen looks identical whether a model is answering or
          * the built-in matcher is, and the difference is enormous: one can answer
          * anything, the other recognises a couple of dozen questions and says so
          * when it cannot. Jay plugged a key in, it did not work, the key silently
          * stayed unused — and the next canned answer read as the assistant being
          * stupid rather than as the model never having been reached. One word in
          * the corner is the whole of the fix, and it is also how anybody checks a
          * new key is live without having to think of a question no matcher could
          * answer.
          */}
        <p className={`assist-who is-${answeredBy(a.settings)}`} title={describeSetup(a.settings, lang)}>
          <Icon name="assistant" />
          <span>{t(`assistant.by.${answeredBy(a.settings)}` as 'assistant.by.local')}</span>
        </p>

        {/*
          * The way out, at every stage: it throws away a recording, calls back a
          * question on its way, silences an answer and closes. Drawn above the
          * stage — on a phone the stage spans the whole width, and it used to sit
          * on top of this button and swallow every tap on it.
          */}
        <button type="button" className="assist-x is-back" onClick={a.close} aria-label={t('assistant.stopAll')} title={t('assistant.stopAll')}>
          <Icon name="close" />
        </button>

        <div className="assist-stage" ref={stageRef}>
          <div className="assist-slot-say">{said}</div>

          <div className="assist-slot-mic">
            {stage === 'ready' && typing ? (
              <form
                className="assist-type"
                onSubmit={(e) => {
                  e.preventDefault();
                  a.askText(draft);
                  setDraft('');
                }}
              >
                <input
                  ref={typeRef}
                  className="input assist-type-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('assistant.placeholder')}
                  aria-label={t('assistant.placeholder')}
                />
                <button type="submit" className="btn btn-primary assist-type-send" disabled={!draft.trim()} aria-label={t('assistant.send')}>
                  <Icon name="forward" className="icon-mirror" />
                </button>
              </form>
            ) : (
              <div ref={flyRef} className={`assist-fly ${banded ? 'is-going-home' : ''}`}>
                <TheButton face={face} flying={banded} hearing={a.hearing} label={label} onPress={a.toggleMic} />
              </div>
            )}
          </div>

          {/*
            * Always rendered, even when it says nothing. A line that appears and
            * disappears takes its gap with it, and the button beneath it shifts —
            * which is the one thing this screen is not allowed to do.
            */}
          <p className={`assist-hint ${urgent ? 'is-urgent' : ''}`}>{hint || ' '}</p>

          <div ref={extrasRef} className={`assist-slot-extras ${formShown ? 'is-tall' : ''}`}>
            {stage === 'answer' && answer?.offer && <ReportOffer offer={answer.offer} />}

            {/*
              * The way in for somebody who would rather type — a keyboard, small
              * and unlabelled at the very bottom. Deliberately not obvious: almost
              * nobody here wants it, and a spelled-out "Type instead" under the
              * microphone reads as an equal choice and makes people stop and pick.
              */}
            {stage === 'ready' && a.canListen && (
              <button
                type="button"
                className="assist-switch"
                onClick={() => setTyping((v) => !v)}
                aria-label={typing ? t('assistant.useVoice') : t('assistant.useKeyboard')}
                title={typing ? t('assistant.useVoice') : t('assistant.useKeyboard')}
              >
                <Icon name={typing ? 'mic' : 'keyboard'} />
              </button>
            )}
          </div>
        </div>
      </div>

      {banded && answer && <AnswerBand answer={answer} origin={origin} onDismiss={a.close} speaking={a.speaking} />}
    </>
  );
}

/**
 * The answer, across the top of the screen they are actually looking at.
 *
 * This is the part that makes a spoken assistant work rather than merely talk.
 * Almost every answer here has just *done* something — moved to the tree,
 * centred on somebody, opened a panel — and an answer that covers the screen
 * while describing what it did to that screen is an answer nobody can follow.
 * So the dark goes, the result is underneath in full, and the words sit in a band
 * at the top being read out while the person looks at the thing they asked for.
 *
 * It does not trap anybody: the rest of the screen is live the whole time, and
 * the band leaves on its own when the reading stops.
 */
function AnswerBand({
  answer,
  origin,
  onDismiss,
  speaking,
}: {
  answer: Answer;
  origin: Origin | null;
  onDismiss: () => void;
  speaking: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className={`assist-band ${answer.error ? 'is-error' : ''}`} role="status" aria-live="polite">
      <div className="assist-band-inner">
        <Icon name={speaking ? 'volume' : 'assistant'} className="assist-band-mark" />
        <div className="assist-band-text">
          <p className="assist-band-say">{answer.text}</p>
          {/* What the app actually did, so nobody wonders whether anything happened. */}
          {answer.did.map((line, i) => (
            <p key={i} className="assist-band-did">
              <Icon name="check" />
              <span>{line}</span>
            </p>
          ))}
        </div>
        <button type="button" className="assist-band-x is-back" onClick={onDismiss} aria-label={t('nav.close')} title={t('nav.close')}>
          <Icon name="close" />
        </button>
      </div>
      {/*
        * A thread back to the corner the button just flew into.
        *
        * Without it the band is a notification that arrived from nowhere; with
        * it, it is plainly the thing that was in the middle of the screen a
        * moment ago, now finished and parked. It is drawn only when we know
        * where the corner is, and it is decoration, so it never takes a click.
        */}
      {origin && <span className="assist-band-tail" style={{ left: origin.x }} aria-hidden="true" />}
    </div>
  );
}

/** What the one button is doing right now. */
type ButtonFace = 'mic' | 'done' | 'think';

/**
 * The one button.
 *
 * Everything the assistant asks of a person happens here, and it never moves:
 * the stop, and the way to call a question back, are the same circle in the same
 * place wearing a different face. That is the whole design. A screen where the
 * thing you press jumps somewhere else between steps is a screen people stop
 * trusting, and this one is for people who did not trust themselves with it to
 * begin with.
 *
 * Hold-to-talk used to live here, with pointer capture and a timer to tell a tap
 * from a hold. All of it is gone. The corner button starts the recording now, so
 * there is no press left to hold — and a plain `click` is also what fixed the
 * phone bug that came with the old gesture, where the click a phone holds back
 * for a third of a second after your finger lifts would land on a button that had
 * meanwhile turned into the cancel, and throw the question away. One handler that
 * can only ever mean one thing cannot do that.
 */
function TheButton({
  face,
  flying,
  hearing,
  label,
  onPress,
}: {
  face: ButtonFace;
  /** On its way home to the corner: no longer pressable, and fading as it goes. */
  flying: boolean;
  /** Sound is arriving right now: the ripples swell. */
  hearing: boolean;
  label: string;
  onPress: () => void;
}) {
  const thinking = face === 'think';
  return (
    <button
      type="button"
      // `is-stop` is kept on the listening face for the browser check script.
      className={`assist-mic-big is-${face} ${face === 'done' ? 'is-stop' : ''} ${hearing ? 'is-hearing' : ''} ${flying ? 'is-going-home' : ''}`}
      onClick={flying || thinking ? undefined : onPress}
      // A long press on a touch screen otherwise raises the text-selection menu
      // right on top of the thing being held down.
      onContextMenu={(e) => e.preventDefault()}
      aria-label={label}
      aria-busy={thinking || undefined}
      aria-disabled={thinking || undefined}
      title={label}
      tabIndex={flying ? -1 : undefined}
    >
      {/* Listening: rings that keep leaving the button, bigger while they talk. */}
      {face === 'done' && (
        <span className="assist-ripples" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      )}
      {/* Thinking: one arc turning round the edge — busy, not waiting for a press. */}
      {thinking && (
        <svg className="assist-spin" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="assist-spin-track" cx="50" cy="50" r="46" />
          <circle className="assist-spin-arc" cx="50" cy="50" r="46" />
        </svg>
      )}
      {thinking ? (
        <span className="assist-dots assist-dots-big" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      ) : (
        <Icon name={face === 'done' ? 'check' : 'mic'} />
      )}
    </button>
  );
}

/** One press to tell the administrators. Everything about where they are is already attached. */
function ReportOffer({ offer }: { offer: NonNullable<Answer['offer']> }) {
  const { t } = useI18n();
  const a = useAssistant();
  const [title, setTitle] = useState(offer.title);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!offer.title) inputRef.current?.focus();
  }, [offer.title]);

  if (sent) {
    return (
      <p className="assist-sent" role="status">
        <Icon name="check" />
        <span>{t('assistant.report.sent')}</span>
      </p>
    );
  }

  return (
    <div className="assist-report">
      <p className="assist-report-title">{t(`assistant.report.kind.${offer.kind}` as 'assistant.report.kind.fault')}</p>
      {/* One field. Everything else an administrator needs was gathered already. */}
      <input
        ref={inputRef}
        className="input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('assistant.report.titleHint')}
        aria-label={t('assistant.report.titleHint')}
      />
      <button
        type="button"
        className="btn btn-primary btn-lg"
        disabled={busy || !title.trim()}
        onClick={() => {
          setBusy(true);
          void a.sendReport({ kind: offer.kind, title: title.trim(), detail: offer.detail }).then((ok) => {
            setBusy(false);
            setSent(ok);
          });
        }}
      >
        <Icon name="mail" />
        <span>{t('assistant.report.send')}</span>
      </button>
      <p className="assist-report-note">{t('assistant.report.attached')}</p>
    </div>
  );
}
